import { Box, Flex, Text, VStack, Grid, Spinner } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { LuShieldCheck, LuShieldAlert, LuShieldQuestion, LuDatabase, LuArrowRight } from 'react-icons/lu'
import { themeColors, brand, statusColors } from '../../theme'
import { useRequest } from '../../hooks/useRequest'
import { auditService } from '../../api/services/audit.service'
import type { AuditTableSummary } from '../../api/models/response'
import { T, useT } from '../../i18n'

const MotionBox = motion.create(Box)

const entityLabelKeys: Record<string, string> = {
  Lote: 'auditoria.entities.mineralBatches',
  ShipmentRecord: 'auditoria.entities.transportRecords',
  ShipmentLocationPoint: 'auditoria.entities.locationPoints',
  ShipmentPhoto: 'auditoria.entities.transportPhotos',
  Recepcion: 'auditoria.entities.receptions',
  MiningSupplier: 'auditoria.entities.miningSuppliers',
  MiningRight: 'auditoria.entities.miningRights',
  MiningRightComponent: 'auditoria.entities.dmComponents',
}

export default function AuditoriaPage() {
  const navigate = useNavigate()
  const colors = themeColors
  const { t } = useT()

  const { data: summaries, isFetching: loading } = useRequest(auditService.getSummary, { onInit: true })
  const { execute: seedBaseline, isFetching: isSeeding } = useRequest(auditService.seedBaseline, {
    successMessage: 'Baseline de auditoria generado exitosamente',
  })

  const getStatusIcon = (item: AuditTableSummary) => {
    if (!item.lastVerification) return <LuShieldQuestion size={28} color={colors.textDimmed} />
    if (item.lastVerification.tamperedCount === 0) return <LuShieldCheck size={28} color={statusColors.success} />
    return <LuShieldAlert size={28} color={statusColors.error} />
  }

  const getStatusBg = (item: AuditTableSummary) => {
    if (!item.lastVerification) return colors.bgCard
    if (item.lastVerification.tamperedCount === 0) return '#eef7f6'
    return '#fcefeb'
  }

  const getStatusBorder = (item: AuditTableSummary) => {
    if (!item.lastVerification) return colors.border
    if (item.lastVerification.tamperedCount === 0) return '#bfe3df'
    return '#FECACA'
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('es-PE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <VStack gap={6} alignItems="stretch">
      <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <Flex justifyContent="space-between" alignItems="center" mb={6}>
          <Box>
            <T tid="auditoria.title" fontSize="xl" fontWeight="700" color={colors.text} />
            <T tid="auditoria.subtitle" fontSize="sm" color={colors.textMuted} />
          </Box>
          <Box
            as="button"
            px={4}
            py={2}
            borderRadius="lg"
            fontSize="sm"
            fontWeight="600"
            bg={colors.bgCard}
            color={colors.text}
            border="1px solid"
            borderColor={colors.border}
            cursor="pointer"
            _hover={{ bg: colors.bgHover }}
            onClick={() => seedBaseline(undefined as never)}
            opacity={isSeeding ? 0.6 : 1}
          >
            <Flex alignItems="center" gap={2}>
              {isSeeding ? <Spinner size="xs" /> : <LuDatabase size={16} />}
              <T tid="auditoria.seedBaseline" />
            </Flex>
          </Box>
        </Flex>

        {loading && (
          <Flex justifyContent="center" py={12}>
            <Spinner size="lg" color={brand.green} />
          </Flex>
        )}

        <Grid
          templateColumns={{
            base: '1fr',
            md: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
            xl: 'repeat(4, 1fr)',
          }}
          gap={4}
        >
          {(summaries || []).map((item, index) => (
            <MotionBox
              key={item.entityName}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              bg={getStatusBg(item)}
              border="1px solid"
              borderColor={getStatusBorder(item)}
              borderRadius="xl"
              p={5}
              _hover={{ boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
            >
              <Flex justifyContent="space-between" alignItems="flex-start" mb={3}>
                {getStatusIcon(item)}
                <Text fontSize="xs" fontWeight="600" color={colors.textMuted}>
                  {item.totalRecords} {t('auditoria.records')}
                </Text>
              </Flex>

              <T tid={entityLabelKeys[item.entityName]} fontSize="sm" fontWeight="600" color={colors.text} mb={1} />
              <Text fontSize="xs" color={colors.textMuted} mb={3}>
                {item.entityName}
              </Text>

              {item.lastVerification ? (
                <Box fontSize="xs" mb={3}>
                  <Text color={colors.textDimmed}>
                    {t('auditoria.lastVerification')}{formatDate(item.lastVerification.verifiedAt)}
                  </Text>
                  <Text color={colors.textDimmed}>
                    {t('auditoria.verifiedBy')}{item.lastVerification.verifiedBy} ({item.lastVerification.verificationType})
                  </Text>
                  <Flex gap={3} mt={1}>
                    <Text color={statusColors.success} fontWeight="600">
                      {item.lastVerification.validCount} {t('auditoria.valid')}
                    </Text>
                    {item.lastVerification.tamperedCount > 0 && (
                      <Text color={statusColors.error} fontWeight="600">
                        {item.lastVerification.tamperedCount} {t('auditoria.altered')}
                      </Text>
                    )}
                  </Flex>
                </Box>
              ) : (
                <T tid="auditoria.notVerified" fontSize="xs" color={colors.textDimmed} mb={3} />
              )}

              <Box
                as="button"
                w="100%"
                px={3}
                py={2}
                borderRadius="lg"
                fontSize="xs"
                fontWeight="600"
                bg={brand.green}
                color="white"
                cursor="pointer"
                _hover={{ bg: brand.greenDark }}
                onClick={() => navigate(`/app/auditoria/${item.entityName}`)}
              >
                <Flex alignItems="center" justifyContent="center" gap={2}>
                  <T tid="auditoria.viewDetail" />
                  <LuArrowRight size={14} />
                </Flex>
              </Box>
            </MotionBox>
          ))}
        </Grid>
      </MotionBox>
    </VStack>
  )
}
